import type {
  AuthResponse,
  OrderData,
  BatchOrdersData,
  OrderResponse,
  OrderSyncResponse,
  OrderTestResponse,
  BatchOrdersResponse,
  CancelOrderResponse,
  CancelPartialOrderData,
  ModifyOrderData,
  ModifyOrderResponse,
  OrderDetails,
  OpenOrdersResponse,
  ClosedOrdersResponse,
  TradeHistoryResponse,
  SymbolsWithOpenOrderResponse,
  DCPResponse,
  SetDCPData,
  StopOrderData,
  StopOrderResponse,
  StopOrdersListResponse,
  OcoOrderData,
  OcoOrderResponse,
  OcoOrderDetailResponse,
  OcoOrderListResponse,
  BatchCancelResponse,
  OrderHistoryResponse
} from "../types/order.types";
import { authApi } from "./api.service";

// ================= Add Orders ================= //

export const addOrder = async (orderData: OrderData): Promise<OrderResponse> => {
  const response = await authApi.post<OrderResponse>('/v0/kucoin/user/spot/orders', orderData);
  return response.data;
};

export const addOrderSync = async (orderData: OrderData): Promise<OrderSyncResponse> => {
  const response = await authApi.post<OrderSyncResponse>('/v0/kucoin/user/spot/orders/sync', orderData);
  return response.data;
};

export const addOrderTest = async (orderData: OrderData): Promise<OrderTestResponse> => {
  const response = await authApi.post<OrderTestResponse>('/v0/kucoin/user/spot/orders/test', orderData);
  return response.data;
};

export const batchAddOrders = async (ordersData: BatchOrdersData): Promise<BatchOrdersResponse> => {
  const response = await authApi.post<BatchOrdersResponse>('/v0/kucoin/user/spot/orders/batch', ordersData);
  return response.data;
};

export const batchAddOrdersSync = async (ordersData: BatchOrdersData): Promise<BatchOrdersResponse> => {
  const response = await authApi.post<BatchOrdersResponse>('/v0/kucoin/user/spot/orders/batch/sync', ordersData);
  return response.data;
};

// ================= Cancel Orders ================= //

export const cancelOrderByOrderId = async (orderId: string): Promise<CancelOrderResponse> => {
  const response = await authApi.delete<CancelOrderResponse>(`/v0/kucoin/user/spot/orders/order/${orderId}`);
  return response.data;
};

export const cancelOrderByOrderIdSync = async (orderId: string): Promise<CancelOrderResponse> => {
  const response = await authApi.delete<CancelOrderResponse>(`/v0/kucoin/user/spot/orders/order/${orderId}/sync`);
  return response.data;
};

export const cancelOrderByClientOid = async (clientOid: string): Promise<CancelOrderResponse> => {
  const response = await authApi.delete<CancelOrderResponse>(`/v0/kucoin/user/spot/orders/client/${clientOid}`);
  return response.data;
};

export const cancelOrderByClientOidSync = async (clientOid: string): Promise<CancelOrderResponse> => {
  const response = await authApi.delete<CancelOrderResponse>(`/v0/kucoin/user/spot/orders/client/${clientOid}/sync`);
  return response.data;
};

export const cancelPartialOrder = async (cancelData: CancelPartialOrderData): Promise<CancelOrderResponse> => {
  const params = new URLSearchParams();
  params.append('orderId', cancelData.orderId);
  if (cancelData.cancelSize) {
    params.append('cancelSize', cancelData.cancelSize);
  }

  const response = await authApi.delete<CancelOrderResponse>(`/v0/kucoin/user/spot/orders/partial?${params.toString()}`);
  return response.data;
};

export const cancelAllOrdersBySymbol = async (symbol: string): Promise<CancelOrderResponse> => {
  const response = await authApi.delete<CancelOrderResponse>(`/v0/kucoin/user/spot/orders/symbol/${symbol}`);
  return response.data;
};

export const cancelAllOrders = async (): Promise<CancelOrderResponse> => {
  const response = await authApi.delete<CancelOrderResponse>('/v0/kucoin/user/spot/orders/all');
  return response.data;
};

// ================= Modify Orders ================= //

export const modifyOrder = async (orderId: string, modifyData: ModifyOrderData): Promise<ModifyOrderResponse> => {
  const response = await authApi.put<ModifyOrderResponse>(`/v0/kucoin/user/spot/orders/order/${orderId}`, modifyData);
  return response.data;
};

// ================= Get Orders ================= //

export const getOrderByOrderId = async (orderId: string): Promise<OrderDetails> => {
  const response = await authApi.get<OrderDetails>(`/v0/kucoin/user/spot/orders/order/${orderId}`);
  return response.data;
};

export const getOrderByClientOid = async (clientOid: string): Promise<OrderDetails> => {
  const response = await authApi.get<OrderDetails>(`/v0/kucoin/user/spot/orders/client/${clientOid}`);
  return response.data;
};

export const getSymbolsWithOpenOrder = async (): Promise<SymbolsWithOpenOrderResponse> => {
  const response = await authApi.get<SymbolsWithOpenOrderResponse>('/v0/kucoin/user/spot/orders/symbols/open');
  return response.data;
};

export const getOpenOrders = async (symbol?: string): Promise<OpenOrdersResponse> => {
  const params = new URLSearchParams();
  if (symbol) {
    params.append('symbol', symbol);
  }

  const queryString = params.toString();
  const url = `/v0/kucoin/user/spot/orders/open${queryString ? `?${queryString}` : ''}`;

  const response = await authApi.get<OpenOrdersResponse>(url);
  return response.data;
};

export const getOpenOrdersByPage = async (
  symbol?: string,
  type?: string,
  status?: string,
  page: number = 1,
  limit: number = 50
): Promise<OpenOrdersResponse> => {
  const params = new URLSearchParams();
  if (symbol) params.append("symbol", symbol);
  if (type) params.append("type", type);
  if (status) params.append("status", status);
  params.append("page", page.toString());
  params.append("limit", limit.toString());

  const url = `/v0/kucoin/user/spot/orders/database?${params.toString()}`;
  const response = await authApi.get<OpenOrdersResponse>(url);
  return response.data;
};

export const getAllOpenOrdersForSymbol = async (
  symbol?: string,
): Promise<OpenOrdersResponse> => {
  const params = new URLSearchParams();
  if (symbol) params.append("symbol", symbol);
  const url = `/v0/kucoin/user/spot/orders/open?${params.toString()}`;
  const response = await authApi.get<OpenOrdersResponse>(url);
  return response.data;
};

export const getOrderHistory = async (
  symbol?: string,
  orderType?: string,
): Promise<OrderHistoryResponse> => {
  const params = new URLSearchParams();
  if (symbol) {
    params.append('symbol', symbol);
  }
  if(orderType) params.append('orderType', orderType || '');
  const queryString = params.toString();
  const url = `/v0/kucoin/user/spot/orders/history${queryString ? `?${queryString}` : ''}`;

  const response = await authApi.get<OrderHistoryResponse>(url);
  return response.data;
}

export const getClosedOrders = async (
  symbol?: string,
  side?: string,
  type?: string,
  startAt?: number,
  endAt?: number
): Promise<ClosedOrdersResponse> => {
  const params = new URLSearchParams();
  if (symbol) {
    params.append('symbol', symbol);
  }
  if (side) {
    params.append('side', side);
  }
  if (type) {
    params.append('type', type);
  }
  if (startAt) {
    params.append('startAt', startAt.toString());
  }
  if (endAt) {
    params.append('endAt', endAt.toString());
  }

  const queryString = params.toString();
  const url = `/v0/kucoin/user/spot/orders/closed${queryString ? `?${queryString}` : ''}`;

  const response = await authApi.get<ClosedOrdersResponse>(url);
  return response.data;
};


// ================= DCP (Daily Cancel Protection) ================= //

export const getDCP = async (): Promise<DCPResponse> => {
  const response = await authApi.get<DCPResponse>('/v0/kucoin/user/spot/orders/dcp');
  return response.data;
};

export const setDCP = async (dcpData: SetDCPData): Promise<DCPResponse> => {
  const response = await authApi.post<DCPResponse>('/v0/kucoin/user/spot/orders/dcp', dcpData);
  return response.data;
};

// ================= Stop Orders ================= //

export const addStopOrder = async (stopOrderData: StopOrderData): Promise<StopOrderResponse> => {
  const response = await authApi.post<StopOrderResponse>('/v0/kucoin/user/spot/orders/stop', stopOrderData);
  return response.data;
};

export const cancelStopOrderByClientOid = async (clientOid: string): Promise<CancelOrderResponse> => {
  const response = await authApi.delete<CancelOrderResponse>(`/v0/kucoin/user/spot/orders/stop/client/${clientOid}`);
  return response.data;
};

export const cancelStopOrderByOrderId = async (orderId: string): Promise<CancelOrderResponse> => {
  const response = await authApi.delete<CancelOrderResponse>(`/v0/kucoin/user/spot/orders/stop/${orderId}`);
  return response.data;
};

export const batchCancelStopOrders = async (orderIds: string[]): Promise<BatchCancelResponse> => {
  const response = await authApi.post<BatchCancelResponse>('/v0/kucoin/user/spot/orders/stop/batch', {
    orderIds
  });
  return response.data;
};

export const getStopOrdersList = async (
  symbol?: string,
  side?: string,
  type?: string,
  startAt?: number,
  endAt?: number,
  status?: string
): Promise<StopOrdersListResponse> => {
  const params = new URLSearchParams();
  if (symbol) {
    params.append('symbol', symbol);
  }
  if (side) {
    params.append('side', side);
  }
  if (type) {
    params.append('type', type);
  }
  if (startAt) {
    params.append('startAt', startAt.toString());
  }
  if (endAt) {
    params.append('endAt', endAt.toString());
  }
  if (status) {
    params.append('status', status);
  }

  const queryString = params.toString();
  const url = `/v0/kucoin/user/spot/orders/stop${queryString ? `?${queryString}` : ''}`;

  const response = await authApi.get<StopOrdersListResponse>(url);
  return response.data;
};

export const getStopOrderByOrderId = async (orderId: string): Promise<StopOrderResponse> => {
  const response = await authApi.get<StopOrderResponse>(`/v0/kucoin/user/spot/orders/stop/${orderId}`);
  return response.data;
};

export const getStopOrderByClientOid = async (clientOid: string): Promise<StopOrderResponse> => {
  const response = await authApi.get<StopOrderResponse>(`/v0/kucoin/user/spot/orders/stop/client/${clientOid}`);
  return response.data;
};

// ================= OCO Orders ================= //

export const addOcoOrder = async (ocoOrderData: OcoOrderData): Promise<OcoOrderResponse> => {
  const response = await authApi.post<OcoOrderResponse>('/v0/kucoin/user/spot/orders/oco', ocoOrderData);
  return response.data;
};

export const cancelOcoOrderByOrderId = async (orderId: string): Promise<CancelOrderResponse> => {
  const response = await authApi.delete<CancelOrderResponse>(`/v0/kucoin/user/spot/orders/oco/${orderId}`);
  return response.data;
};

export const cancelOcoOrderByClientOid = async (clientOid: string): Promise<CancelOrderResponse> => {
  const response = await authApi.delete<CancelOrderResponse>(`/v0/kucoin/user/spot/orders/oco/client/${clientOid}`);
  return response.data;
};

export const batchCancelOcoOrders = async (orderIds: string[]): Promise<BatchCancelResponse> => {
  const response = await authApi.post<BatchCancelResponse>('/v0/kucoin/user/spot/orders/oco/batch', {
    orderIds
  });
  return response.data;
};

export const getOcoOrderByOrderId = async (orderId: string): Promise<OcoOrderResponse> => {
  const response = await authApi.get<OcoOrderResponse>(`/v0/kucoin/user/spot/orders/oco/${orderId}`);
  return response.data;
};

export const getOcoOrderByClientOid = async (clientOid: string): Promise<OcoOrderResponse> => {
  const response = await authApi.get<OcoOrderResponse>(`/v0/kucoin/user/spot/orders/oco/client/${clientOid}`);
  return response.data;
};

export const getOcoOrderDetailByOrderId = async (orderId: string): Promise<OcoOrderDetailResponse> => {
  const response = await authApi.get<OcoOrderDetailResponse>(`/v0/kucoin/user/spot/orders/oco/detail/${orderId}`);
  return response.data;
};

export const getOcoOrderList = async (
  symbol?: string,
  side?: string,
  status?: string,
  startAt?: number,
  endAt?: number
): Promise<OcoOrderListResponse> => {
  const params = new URLSearchParams();
  if (symbol) {
    params.append('symbol', symbol);
  }
  if (side) {
    params.append('side', side);
  }
  if (status) {
    params.append('status', status);
  }
  if (startAt) {
    params.append('startAt', startAt.toString());
  }
  if (endAt) {
    params.append('endAt', endAt.toString());
  }

  const queryString = params.toString();
  const url = `/v0/kucoin/user/spot/orders/oco${queryString ? `?${queryString}` : ''}`;

  const response = await authApi.get<OcoOrderListResponse>(url);
  return response.data;
};

export const pingCexServer = async () => {
  const response = await authApi.get<{
    success: boolean;
    message: string;
    userId: string;
    connectionId: string;
    expiresIn: number;
    timestamp: string;
  }>('/v0/kucoin/user/spot/orders/ping');
  return response.data;
};